import { useState } from 'react'
import { Instagram, Facebook, Linkedin, Twitter, Youtube, Globe, TrendingUp, Pencil, Trash2, Loader2 } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Badge } from '@/components/common/Badge'
import { cn } from '@/lib/utils'
import { advApi, getApiErrorMessage } from '@/services/api'
import type { AdCampaign, CampaignStatus, Platform } from '@/types'

const PLATFORM_ICON: Record<Platform, React.ReactNode> = {
  INSTAGRAM: <Instagram className="w-3.5 h-3.5" />,
  FACEBOOK: <Facebook className="w-3.5 h-3.5" />,
  LINKEDIN: <Linkedin className="w-3.5 h-3.5" />,
  TWITTER: <Twitter className="w-3.5 h-3.5" />,
  TIKTOK: <TrendingUp className="w-3.5 h-3.5" />,
  YOUTUBE: <Youtube className="w-3.5 h-3.5" />,
  PINTEREST: <Globe className="w-3.5 h-3.5" />,
}

const PLATFORM_LABEL: Record<Platform, string> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  LINKEDIN: 'LinkedIn',
  TWITTER: 'Twitter / X',
  TIKTOK: 'TikTok',
  YOUTUBE: 'YouTube',
  PINTEREST: 'Pinterest',
}

const PLATFORM_COLOR: Record<Platform, string> = {
  INSTAGRAM: 'bg-pink-100 text-pink-700',
  FACEBOOK: 'bg-blue-100 text-blue-700',
  LINKEDIN: 'bg-sky-100 text-sky-700',
  TWITTER: 'bg-slate-100 text-slate-700',
  TIKTOK: 'bg-purple-100 text-purple-700',
  YOUTUBE: 'bg-red-100 text-red-700',
  PINTEREST: 'bg-red-100 text-red-600',
}

const STATUS_VARIANT: Record<CampaignStatus, 'success' | 'warning' | 'neutral' | 'info' | 'danger'> = {
  ACTIVE: 'success',
  PAUSED: 'warning',
  DRAFT: 'neutral',
  PENDING_REVIEW: 'info',
  COMPLETED: 'info',
  ARCHIVED: 'neutral',
}

const STATUS_LABEL: Record<CampaignStatus, string> = {
  ACTIVE: 'Attiva',
  PAUSED: 'In pausa',
  DRAFT: 'Bozza',
  PENDING_REVIEW: 'In revisione',
  COMPLETED: 'Completata',
  ARCHIVED: 'Archiviata',
}

// Extended campaign with optional performance metrics
type CampaignWithMetrics = AdCampaign & {
  impressions?: number
  clicks?: number
  ctr?: number
  cpc?: number
  roas?: number
}

interface CampaignCardProps {
  campaign: CampaignWithMetrics
  onClick: () => void
  onEdit?: (campaign: CampaignWithMetrics) => void
  onDeleted?: () => void
}

function roasColor(roas: number): string {
  if (roas < 1) return 'text-danger-600'
  if (roas < 2) return 'text-warning-600'
  return 'text-success-600'
}

// ---------------------------------------------------------------
// Delete confirmation dialog
// ---------------------------------------------------------------
interface DeleteConfirmProps {
  campaignName: string
  isDeleting: boolean
  onConfirm: () => void
  onCancel: () => void
}

function DeleteConfirmDialog({ campaignName, isDeleting, onConfirm, onCancel }: DeleteConfirmProps) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-3xl shadow-modal border border-slate-200 w-full max-w-sm p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-2">Elimina campagna</h3>
        <p className="text-sm text-slate-600 mb-1">
          Stai per eliminare la campagna:
        </p>
        <p className="text-sm font-medium text-slate-900 mb-4 truncate">"{campaignName}"</p>
        <p className="text-xs text-slate-500 mb-5">
          Solo le campagne in stato DRAFT possono essere eliminate. Questa azione e' irreversibile.
        </p>
        <div className="flex items-center gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            className={cn(
              'flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-danger-600 rounded-lg',
              'hover:bg-danger-700 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {isDeleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {isDeleting ? 'Eliminazione...' : 'Elimina'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------
// Main CampaignCard
// ---------------------------------------------------------------
export function CampaignCard({ campaign, onClick, onEdit, onDeleted }: CampaignCardProps) {
  const queryClient = useQueryClient()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  const impressions = campaign.impressions ?? 0
  const clicks = campaign.clicks ?? 0
  const ctr = campaign.ctr ?? 0
  const roas = campaign.roas ?? 0

  const spentPct = campaign.totalBudget > 0
    ? Math.min((campaign.spentBudget / campaign.totalBudget) * 100, 100)
    : 0

  const fmt = (n: number, opts?: Intl.NumberFormatOptions) =>
    new Intl.NumberFormat('it-IT', opts).format(n)

  const fmtImpressions = (n: number) =>
    n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(0)}K` : String(n)

  const startDate = new Date(campaign.startDate).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
  const endDate = campaign.endDate
    ? new Date(campaign.endDate).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
    : 'Continua'

  const deleteMutation = useMutation({
    mutationFn: () => advApi.deleteCampaign(campaign.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      setShowDeleteConfirm(false)
      onDeleted?.()
    },
    onError: (err) => {
      setDeleteError(getApiErrorMessage(err))
    },
  })

  function handleEditClick(e: React.MouseEvent) {
    e.stopPropagation()
    onEdit?.(campaign)
  }

  function handleDeleteClick(e: React.MouseEvent) {
    e.stopPropagation()
    setDeleteError('')
    setShowDeleteConfirm(true)
  }

  return (
    <>
      <div
        className="bg-white rounded-2xl border border-slate-200 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] cursor-pointer p-4 space-y-3 group relative"
        onClick={onClick}
      >
        {/* Action buttons — appear on hover */}
        {(onEdit || onDeleted) && (
          <div
            className={cn(
              'absolute top-3 right-3 flex items-center gap-1',
              'opacity-0 group-hover:opacity-100 transition-opacity'
            )}
          >
            {onEdit && (
              <button
                type="button"
                onClick={handleEditClick}
                title="Modifica campagna"
                className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {onDeleted && campaign.status === 'DRAFT' && (
              <button
                type="button"
                onClick={handleDeleteClick}
                title="Elimina campagna"
                className="p-1 rounded-md text-slate-400 hover:text-danger-600 hover:bg-danger-50 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 pr-10">
            <p className="text-sm font-semibold text-slate-900 leading-snug line-clamp-2 break-words">{campaign.name}</p>
            <p className="text-xs text-slate-500 truncate mt-0.5">{campaign.client?.name ?? '—'}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {campaign.platforms[0] && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium',
                  PLATFORM_COLOR[campaign.platforms[0]]
                )}
              >
                {PLATFORM_ICON[campaign.platforms[0]]}
                {PLATFORM_LABEL[campaign.platforms[0]]}
              </span>
            )}
          </div>
        </div>

        {/* Status + type */}
        <div className="flex items-center gap-2">
          <Badge variant={STATUS_VARIANT[campaign.status as CampaignStatus]} dot size="sm">
            {STATUS_LABEL[campaign.status as CampaignStatus]}
          </Badge>
          <Badge variant="neutral" size="sm">
            {campaign.type === 'ALWAYS_ON' ? 'Always On' : 'Spot'}
          </Badge>
        </div>

        {/* Budget bar */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-slate-500">Budget speso</span>
            <span className="text-[10px] font-medium text-slate-700 tabular-nums">
              {fmt(campaign.spentBudget, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
              {' / '}
              {fmt(campaign.totalBudget, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
            </span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all',
                spentPct > 90 ? 'bg-danger-500' : spentPct > 70 ? 'bg-warning-500' : 'bg-primary-500'
              )}
              style={{ width: `${spentPct}%` }}
            />
          </div>
        </div>

        {/* Mini metrics */}
        <div className="grid grid-cols-4 gap-2 pt-1 border-t border-slate-200">
          <div>
            <p className="text-[9px] text-slate-500 uppercase tracking-wide">Impr.</p>
            <p className="text-xs font-semibold text-slate-800 tabular-nums">{fmtImpressions(impressions)}</p>
          </div>
          <div>
            <p className="text-[9px] text-slate-500 uppercase tracking-wide">Click</p>
            <p className="text-xs font-semibold text-slate-800 tabular-nums">{fmtImpressions(clicks)}</p>
          </div>
          <div>
            <p className="text-[9px] text-slate-500 uppercase tracking-wide">CTR</p>
            <p className="text-xs font-semibold text-slate-800 tabular-nums">{ctr.toFixed(2)}%</p>
          </div>
          <div>
            <p className="text-[9px] text-slate-500 uppercase tracking-wide">ROAS</p>
            <p className={cn('text-xs font-bold tabular-nums', roasColor(roas))}>
              {roas > 0 ? `${roas.toFixed(1)}x` : '—'}
            </p>
          </div>
        </div>

        {/* Date range */}
        <p className="text-[10px] text-slate-500 pt-0.5 tabular-nums">
          {startDate} → {endDate}
        </p>

        {/* Delete error inline */}
        {deleteError && (
          <p className="text-xs text-danger-600 bg-danger-50 rounded-md px-2 py-1">
            {deleteError}
          </p>
        )}
      </div>

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <DeleteConfirmDialog
          campaignName={campaign.name}
          isDeleting={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => {
            if (!deleteMutation.isPending) setShowDeleteConfirm(false)
          }}
        />
      )}
    </>
  )
}
